/**
 * IndexedDB storage adapter for persistent event queue
 * Provides offline-capable event storage with automatic eviction
 * 
 * Optimized for performance:
 * - Lazy initialization (only initializes on first use)
 * - Transaction batching for improved throughput
 * - Minimal synchronous operations
 */

import { EventQueue, StoredEvent } from '../core/EventQueue';
import { TrackingEvent } from '../types/events';

interface PendingOperation {
  type: 'enqueue' | 'remove';
  data: StoredEvent | string[];
  resolve: () => void;
  reject: (error: Error) => void;
}

export class IndexedDBAdapter implements EventQueue {
  private dbName = 'tracking-events';
  private storeName = 'events';
  private maxSize = 1000;
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private initialized = false;
  
  // Batching support
  private pendingOperations: PendingOperation[] = [];
  private batchTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly batchDelayMs = 10; // Batch operations within 10ms window

  /**
   * Initialize IndexedDB database and object store
   * Uses lazy initialization - only called on first use
   */
  private async init(): Promise<void> {
    // Return existing initialization if in progress
    if (this.initPromise) {
      return this.initPromise;
    }

    // Create new initialization promise
    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => {
        reject(new Error(`Failed to open IndexedDB: ${request.error?.message}`));
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.initialized = true;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, { keyPath: 'id' });
          // Create index on timestamp for efficient querying
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Get database instance, initializing lazily if necessary
   */
  private async getDB(): Promise<IDBDatabase> {
    if (!this.initialized) {
      await this.init();
    }
    if (!this.db) {
      throw new Error('Failed to initialize IndexedDB');
    }
    return this.db;
  }

  /**
   * Process batched operations in a single transaction
   * Significantly improves performance by reducing transaction overhead
   */
  private async processBatch(): Promise<void> {
    if (this.pendingOperations.length === 0) {
      return;
    }

    const operations = this.pendingOperations.splice(0, this.pendingOperations.length);
    
    try {
      const db = await this.getDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      // Process all pending operations in a single transaction
      for (const op of operations) {
        try {
          if (op.type === 'enqueue') {
            store.add(op.data as StoredEvent);
          } else if (op.type === 'remove') {
            const ids = op.data as string[];
            for (const id of ids) {
              store.delete(id);
            }
          }
        } catch (error) {
          op.reject(error as Error);
        }
      }

      // Wait for transaction to complete
      await new Promise<void>((resolve, reject) => {
        transaction.oncomplete = () => {
          // Resolve all pending operations
          operations.forEach(op => op.resolve());
          resolve();
        };
        transaction.onerror = () => {
          // Reject all pending operations
          const error = new Error(`Transaction failed: ${transaction.error?.message}`);
          operations.forEach(op => op.reject(error));
          reject(error);
        };
      });
    } catch (error) {
      // Reject all pending operations on failure
      operations.forEach(op => op.reject(error as Error));
    }
  }

  /**
   * Schedule batch processing with debouncing
   */
  private scheduleBatch(): void {
    if (this.batchTimeout) {
      return; // Already scheduled
    }

    this.batchTimeout = setTimeout(() => {
      this.batchTimeout = null;
      this.processBatch().catch(error => {
        console.error('Batch processing failed:', error);
      });
    }, this.batchDelayMs);
  }

  /**
   * Generate unique ID for events
   * Uses high-resolution timestamp for uniqueness without crypto overhead
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Enqueue an event to IndexedDB storage
   * Optimized for minimal latency - uses batching for actual DB write
   * Enforces max size limit with oldest event eviction
   */
  async enqueue(event: TrackingEvent): Promise<void> {
    // Create stored event with metadata synchronously
    // This is fast and doesn't block on I/O
    const storedEvent: StoredEvent = {
      id: this.generateId(),
      event,
      timestamp: Date.now(),
      retryCount: 0,
      lastAttemptTimestamp: null
    };

    // Queue the operation for batched processing
    // This returns immediately, minimizing trackEvent() latency
    return new Promise<void>((resolve, reject) => {
      this.pendingOperations.push({
        type: 'enqueue',
        data: storedEvent,
        resolve,
        reject
      });
      
      // Schedule batch processing
      this.scheduleBatch();
      
      // Note: Size checking and eviction happens asynchronously
      // This is acceptable as it's a best-effort mechanism
      this.checkAndEvictIfNeeded().catch(error => {
        console.warn('Background eviction failed:', error);
      });
    });
  }

  /**
   * Check queue size and evict oldest events if needed
   * Runs asynchronously to avoid blocking enqueue operations
   */
  private async checkAndEvictIfNeeded(): Promise<void> {
    try {
      const currentSize = await this.size();
      if (currentSize >= this.maxSize) {
        // Evict oldest 100 events to make room
        await this.evictOldest(100);
      }
    } catch (error) {
      // Don't throw - this is a background operation
      console.warn('Size check and eviction failed:', error);
    }
  }

  /**
   * Dequeue events from storage (remove and return)
   */
  async dequeue(count: number): Promise<TrackingEvent[]> {
    const events = await this.peek(count);
    
    // Remove the events we just peeked
    const eventIds = await this.getOldestEventIds(count);
    await this.remove(eventIds);
    
    return events;
  }

  /**
   * Peek at oldest events without removing them
   */
  async peek(count: number): Promise<TrackingEvent[]> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('timestamp');
      const request = index.openCursor(null, 'next');
      
      const events: TrackingEvent[] = [];
      let collected = 0;

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        
        if (cursor && collected < count) {
          const storedEvent = cursor.value as StoredEvent;
          events.push(storedEvent.event);
          collected++;
          cursor.continue();
        } else {
          resolve(events);
        }
      };

      request.onerror = () => reject(new Error(`Failed to peek events: ${request.error?.message}`));
    });
  }

  /**
   * Remove events by their IDs
   * Uses batched transaction for improved performance
   */
  async remove(eventIds: string[]): Promise<void> {
    if (eventIds.length === 0) {
      return;
    }

    // Queue removal operation for batched processing
    return new Promise<void>((resolve, reject) => {
      this.pendingOperations.push({
        type: 'remove',
        data: eventIds,
        resolve,
        reject
      });
      
      // Schedule batch processing
      this.scheduleBatch();
    });
  }

  /**
   * Get current queue size
   */
  async size(): Promise<number> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error(`Failed to get queue size: ${request.error?.message}`));
    });
  }

  /**
   * Clear all events from queue
   */
  async clear(): Promise<void> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to clear queue: ${request.error?.message}`));
    });
  }

  /**
   * Get IDs of oldest events
   * @private
   */
  private async getOldestEventIds(count: number): Promise<string[]> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('timestamp');
      const request = index.openCursor(null, 'next');
      
      const ids: string[] = [];
      let collected = 0;

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        
        if (cursor && collected < count) {
          const storedEvent = cursor.value as StoredEvent;
          ids.push(storedEvent.id);
          collected++;
          cursor.continue();
        } else {
          resolve(ids);
        }
      };

      request.onerror = () => reject(new Error(`Failed to get event IDs: ${request.error?.message}`));
    });
  }

  /**
   * Evict oldest events when queue is full
   * @private
   */
  private async evictOldest(count: number): Promise<void> {
    const ids = await this.getOldestEventIds(count);
    await this.remove(ids);
  }
}
